// src/components/InternalIssueListView.js
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useOutletContext, useNavigate, useParams } from 'react-router-dom';
import DetailedListView from './DetailedListView';
import { departmentQueries, updateQueries } from '../repository/graphQueries';
import { formatDate, formatScore } from '../utils';
import { ISSUE_STATUS_OPTIONS, ISSUE_SEVERITY_OPTIONS, DRI_EXPLANATION } from '../constants';
import EisenhowerMatrixView from './EisenhowerMatrixView';
import { RiInformationLine as InformationCircleIcon } from 'react-icons/ri';

const issueColumns = [
    { header: 'Name', key: 'name', width: '25%' },
    { header: 'DRI', key: 'assigned_to'},
    { header: 'Severity', key: 'severity', width: '10%' },
    { header: 'Status', key: 'status', width: '10%' },
    { header: 'Blocked Initiatives', key: 'blockedInitiatives', formatter: val => Array.isArray(val) ? val.join(', ') : (val || 'N/A'), width: '20%' },
    { header: 'Connections/Score', key: 'score', align: 'center', formatter: formatScore, width: '10%' },
    { header: 'Description', key: 'description', type: 'longText', width: '25%' },
];

const DropdownCheckbox = ({ options, selectedOptions, onChange, title }) => {
    const [isOpen, setIsOpen] = useState(false);

    return (
        <div className="relative inline-block text-left">
            <div>
                <button
                    type="button"
                    className="inline-flex justify-center w-full rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none"
                    onClick={() => setIsOpen(!isOpen)}
                >
                    {title}
                    <svg className="-mr-1 ml-2 h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                        <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                </button>
            </div>

            {isOpen && (
                <div className="origin-top-right absolute right-0 mt-2 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-10">
                    <div className="py-1" role="menu" aria-orientation="vertical">
                        {options.map(option => (
                            <label key={option} className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                                <input
                                    type="checkbox"
                                    className="form-checkbox h-4 w-4 text-indigo-600 transition duration-150 ease-in-out"
                                    checked={selectedOptions.includes(option)}
                                    onChange={() => onChange(option)}
                                />
                                <span className="ml-3">{option}</span>
                            </label>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

// Tooltip component (can be a shared component or defined locally if not already shared)
const Tooltip = ({ content, children }) => {
    const [isVisible, setIsVisible] = useState(false);
    
    return (
        <div className="relative inline-block">
            <div
                className="inline-flex items-center cursor-help"
                onMouseEnter={() => setIsVisible(true)}
                onMouseLeave={() => setIsVisible(false)}
                onClick={() => setIsVisible(!isVisible)} // For mobile support
            >
                {children}
            </div>
            {isVisible && (
                <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-72 p-3 text-sm bg-gray-800 text-white rounded-md shadow-lg z-50">
                    {content}
                    <div className="absolute bottom-full left-1/2 -translate-x-1/2 w-0 h-0 border-x-4 border-x-transparent border-b-4 border-b-gray-800"></div>
                </div>
            )}
        </div>
    );
};

const InternalIssueListView = () => {
    const { neo4jService, departmentId: departmentIdFromContext, dateRange } = useOutletContext();
    const navigate = useNavigate();
    const params = useParams();
    
    const activeRouteDepartmentId = departmentIdFromContext || params.departmentId;
    
    const [departments, setDepartments] = useState([]);
    const [selectedDepartment, setSelectedDepartment] = useState(activeRouteDepartmentId || '');
    const [loadingDepartments, setLoadingDepartments] = useState(true);
    
    const [issuesList, setIssuesList] = useState([]);
    const [loadingIssues, setLoadingIssues] = useState(false);
    const [errorIssues, setErrorIssues] = useState(null);
    const [issueSeverityFilter, setIssueSeverityFilter] = useState('');
    const [issueStatusFilter, setIssueStatusFilter] = useState('');

    const [dris, setDris] = useState([]);
    const [loadingDris, setLoadingDris] = useState(true);
    const [selectedDris, setSelectedDris] = useState([]);
    
    const driCache = useMemo(() => new Map(), []);
    
    const driTooltipContent = DRI_EXPLANATION;

    const loadDris = useCallback(async () => {
        if (!neo4jService || !selectedDepartment) return;
        setLoadingDris(true);
        try {
            const driResult = await neo4jService.runQuery(departmentQueries.getDepartmentDRIs, { departmentId: selectedDepartment });
            setDris(driResult.map(r => r.dri));
        } catch (err) {
            console.error('Error loading DRIs:', err);
        } finally {
            setLoadingDris(false);
        }
    }, [neo4jService, selectedDepartment]);

    useEffect(() => {
        if (selectedDepartment) {
            loadDris();
        }
    }, [selectedDepartment, loadDris]);

    const handleDriChange = (dri) => {
        setSelectedDris(prev =>
            prev.includes(dri) ? prev.filter(d => d !== dri) : [...prev, dri]
        );
    };

    // Load departments
    const loadDepartments = useCallback(async () => {
        if (!neo4jService) return;
        setLoadingDepartments(true);
        try {
            const deptResult = await neo4jService.runQuery(departmentQueries.listAllDepartments);
            const deptList = deptResult.map(r => r.department).filter(d => d);
            setDepartments(deptList);
            
            if (!selectedDepartment && activeRouteDepartmentId && deptList.includes(activeRouteDepartmentId)) {
                setSelectedDepartment(activeRouteDepartmentId);
            }
        } catch (err) {
            console.error('Error loading departments:', err);
        } finally {
            setLoadingDepartments(false);
        }
    }, [neo4jService, selectedDepartment, activeRouteDepartmentId]);

    useEffect(() => {
        loadDepartments();
    }, [loadDepartments]);

    useEffect(() => {
        if (activeRouteDepartmentId && activeRouteDepartmentId !== selectedDepartment) {
            setSelectedDepartment(activeRouteDepartmentId);
        }
    }, [activeRouteDepartmentId, selectedDepartment]);

    const handleDepartmentChange = (event) => {
        const newDept = event.target.value;
        navigate(`/internal/${newDept}/issues`);
    };

    const loadDepartmentIssues = useCallback(async () => {
        if (!neo4jService || !selectedDepartment || !dateRange || !dateRange.start || !dateRange.end) return;
        
        setLoadingIssues(true);
        setErrorIssues(null);
        setIssuesList([]);

        const queryToRun = departmentQueries.getDepartmentIssuesList;
        if (typeof queryToRun !== 'string' || !queryToRun.trim()) {
            console.error("InternalIssueListView: departmentQueries.getDepartmentIssuesList is not a valid Cypher string.", queryToRun);
            setErrorIssues("Internal error: Department issues query definition is missing or invalid. Check graphQueries.js.");
            setLoadingIssues(false);
            return;
        }
        
        try {
            const params = {
                departmentId: selectedDepartment,
                startDate: dateRange.start,
                endDate: dateRange.end,
                severityFilter: issueSeverityFilter || null,
                statusFilter: issueStatusFilter || null,
                driFilter: selectedDris.length > 0 ? selectedDris : null,
            };
            const issues = await neo4jService.runQuery(queryToRun, params);
            setIssuesList(issues);
            
        } catch (err) {
            console.error(`Error loading issues for department ${selectedDepartment}:`, err);
            setErrorIssues(err.message);
            setIssuesList([]);
        } finally {
            setLoadingIssues(false);
        }
    }, [neo4jService, selectedDepartment, dateRange, issueSeverityFilter, issueStatusFilter, selectedDris, driCache]);

    useEffect(() => {
        if (selectedDepartment && dateRange && dateRange.start && dateRange.end) {
            loadDepartmentIssues();
        }
    }, [loadDepartmentIssues, selectedDepartment, dateRange, selectedDris]);

    const handleIssueUpdate = (itemId, updatedField, newValue) => {
        console.log(`Department Issue ${itemId} field ${updatedField} updated to ${newValue}. Refreshing list.`);
        loadDepartmentIssues(); 
    };

    const issueEditableColumnsConfig = {
        status: {
            options: ISSUE_STATUS_OPTIONS,
            query: updateQueries.updateNodeStatus,
            propertyName: 'status'
        },
        severity: {
            options: ISSUE_SEVERITY_OPTIONS,
            query: updateQueries.updateNodeSeverity,
            propertyName: 'severity'
        }
    };

    return (
        <>
            <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-semibold">Department Issues</h2>
                <div className="flex items-center space-x-2">
                    <div className="flex items-center">
                        <DropdownCheckbox
                            options={dris}
                            selectedOptions={selectedDris}
                            onChange={handleDriChange}
                            title="Filter by DRI"
                        />
                        <Tooltip content={driTooltipContent}>
                            <InformationCircleIcon className="h-4 w-4 text-gray-500 ml-1 cursor-help" />
                        </Tooltip>
                    </div>
                    {departments.length > 0 && (
                        <>
                            <label htmlFor="department-select-issues" className="text-sm font-medium text-gray-700">
                                Department:
                            </label>
                            <select
                                id="department-select-issues"
                                value={selectedDepartment}
                                onChange={handleDepartmentChange}
                                className="block w-48 pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                                disabled={loadingDepartments || !departments.length}
                            >
                                {departments.map(dept => (
                                    <option key={dept} value={dept}>{dept}</option>
                                ))}
                            </select>
                        </>
                    )}
                </div>
            </div>
            
            <div className="mb-8">
                <EisenhowerMatrixView 
                    departmentId={selectedDepartment} 
                    clientId={null}
                    typeFilter={['Issue']}
                    driFilter={selectedDris}
                />
            </div>
            <div className="mb-4 p-4 bg-gray-50 rounded-lg border border-gray-200 grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                    <label htmlFor="severityFilterInternalIssues" className="block text-sm font-medium text-gray-700 mb-1">Severity:</label>
                    <select 
                        id="severityFilterInternalIssues" 
                        value={issueSeverityFilter} 
                        onChange={(e) => setIssueSeverityFilter(e.target.value)} 
                        className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                    >
                        <option value="">All</option>
                        {ISSUE_SEVERITY_OPTIONS.map(sev => (<option key={sev} value={sev}>{sev.charAt(0).toUpperCase() + sev.slice(1)}</option>))}
                    </select>
                </div>
                <div>
                    <label htmlFor="statusFilterInternalIssues" className="block text-sm font-medium text-gray-700 mb-1">Status:</label>
                    <select 
                        id="statusFilterInternalIssues" 
                        value={issueStatusFilter} 
                        onChange={(e) => setIssueStatusFilter(e.target.value)} 
                        className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                    >
                        <option value="">All (except Resolved)</option>
                        {ISSUE_STATUS_OPTIONS.map(stat => (<option key={stat} value={stat}>{stat.charAt(0).toUpperCase() + stat.slice(1)}</option>))}
                    </select>
                </div>
            </div>
            <DetailedListView
                title="Issues"
                items={issuesList}
                columns={issueColumns}
                isLoading={loadingIssues}
                error={errorIssues}
                entityType="issues"
                clientId={null} 
                departmentId={selectedDepartment}
                neo4jService={neo4jService}
                onItemUpdate={handleIssueUpdate}
                editableColumnsConfig={issueEditableColumnsConfig}
            />
        </>
    );
};

export default InternalIssueListView;